import confetti from "canvas-confetti";
import { format, fromUnixTime } from "date-fns";
import { useEffect, useState } from "react";
import Masonry from "react-masonry-css";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Avatar from "../../../components/Avatar";
import { FakeBackLinkWithText } from "../../../components/BackLink";
import { DeliverableItem } from "../../../components/Brand/ContentBrief/DeliverableItem";
import { DeliverableMediaReview } from "../../../components/Brand/ContentBrief/DeliverableMediaReview";
import { Card } from "../../../components/Card";
import Chat from "../../../components/Chat";
import { Button, ButtonWrap } from "../../../components/CTA";
import DetailList from "../../../components/DetailList";
import { CardDivider } from "../../../components/Divider";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { AirplaneIcon } from "../../../components/icons/Airplane";
import { BoltIcon } from "../../../components/icons/BoltIcon";
import { MatchingStatusIcon } from "../../../components/icons/briefs/MatchingStatusIcon";
import ChatIcon from "../../../components/icons/ChatIcon";
import { CheckCircle } from "../../../components/icons/CheckCircle";
import { StoryIcon } from "../../../components/icons/Instagram/StoryIcon";
import Loading from "../../../components/Loading";
import { NotificationIndicator } from "../../../components/NotificationIndicator";
import { StarRating } from "../../../components/StarRating";
import { Underline } from "../../../components/Tabs";
import { Text } from "../../../components/Text";
import { ExternalTextLink } from "../../../components/TextLink";
import { View } from "../../../components/View";
import { MOBILE_BREAKPOINT } from "../../../config";
import {
  BrandContentBriefQuery,
  ContentBriefBookingStatus,
  ContentBriefStatus,
  MediaType,
  useBrandContentBriefQuery,
  useUpdateContentBriefBookingMutation,
  useUpdateContentBriefMutation,
} from "../../../graphql/generated";
import useAnalytics from "../../../hooks/useAnalytics";
import useGqlClient from "../../../hooks/useGqlClient";
import { useTheme } from "../../../hooks/useTheme";
import { authSelectors } from "../../../store/auth/selector";
import { actions } from "../../../store/auth/slice";
import styled, { css, ThemeProp } from "../../../styles";
import { ensureProtocol } from "../../../utils/url";
import { getBriefStatusPage } from "../briefs";
import { PreviewBrief } from "./preview";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 960px;
  padding: 0 ${(p) => p.theme.spacing.l};
  box-sizing: border-box;
`;

const SkeletonHeader = styled(Flex)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(p) => p.theme.spacing.xxs};
  margin-bottom: ${(p) => p.theme.spacing.xxs};
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    to right,
    ${(p) => p.theme.color.card.divider} 8%,
    ${(p) => p.theme.color.card.callout} 18%,
    ${(p) => p.theme.color.card.divider} 33%
  );
  background-size: 800px 104px;
  border-radius: 4px;
`;

const SkeletonWrapper = styled(Flex)`
  width: 100%;
`;

const SkeletonAvatarBG = styled(SkeletonBase)`
  border-radius: 999px;
  width: 104px;
  height: 104px;
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${(p) => p.width || "100%"};
  height: ${(p) => p.height || "16px"};
  margin-bottom: ${(p) => p.theme.spacing.xs};
`;

const SkeletonButton = styled(SkeletonBase)`
  width: 100px;
  height: 32px;
  margin: 0 ${(p) => p.theme.spacing.xs};
`;

const SkeletonButtonWrap = styled(Flex)`
  margin-top: ${(p) => p.theme.spacing.s};
  margin-bottom: ${(p) => p.theme.spacing.m};
`;

const SkeletonDivider = styled(SkeletonBase)`
  height: 1px;
  width: 100%;
  margin: ${(p) => p.theme.spacing.l} 0 ${(p) => p.theme.spacing.xl};
`;

const SkeletonDetailItem = styled(Flex)`
  justify-content: space-between;
  margin-bottom: ${(p) => p.theme.spacing.m};
  width: 100%;
`;

const triggerConfetti = () => {
  const end = Date.now() + 1.5 * 1000; // 3 seconds
  const colors = ["#a786ff", "#fd8bbc", "#eca184", "#f8deb1"];

  const frame = () => {
    if (Date.now() > end) return;

    confetti({
      particleCount: 2,
      angle: 60,
      spread: 55,
      startVelocity: 60,
      origin: { x: 0, y: 0.5 },
      colors: colors,
    });
    confetti({
      particleCount: 2,
      angle: 120,
      spread: 55,
      startVelocity: 60,
      origin: { x: 1, y: 0.5 },
      colors: colors,
    });

    requestAnimationFrame(frame);
  };

  frame();
};

export const BriefDetails = () => {
  const client = useGqlClient();
  const { track } = useAnalytics();
  const history = useHistory();
  let { id } = useParams<{ id: string }>();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedMediaIdForReview, setSelectedMediaIdForReview] = useState<
    string | null
  >(null);
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const dispatch = useDispatch();

  const {
    data: briefData,
    refetch: refetchBrief,
    isLoading,
    isError,
  } = useBrandContentBriefQuery(client, {
    id,
  });

  const updateContentBriefMutation = useUpdateContentBriefMutation(client);
  const updateContentBriefBookingMutation =
    useUpdateContentBriefBookingMutation(client);

  useEffect(() => {
    const q = new URLSearchParams(history.location.search);
    const newActiveBrandId = q.get("activeBrandId");

    if (newActiveBrandId && newActiveBrandId !== activeBrandId) {
      dispatch(actions.setActiveBrand({ brandId: newActiveBrandId }));
      dispatch(actions.setActiveGroup({ groupId: "" }));
    }

    if (q.get("chat") === "open") {
      setIsChatOpen(true);
    }
  }, [history.location.search, dispatch, activeBrandId]);

  useEffect(() => {
    refetchBrief();
  }, [activeBrandId, refetchBrief]);

  // Track brief viewing when data loads
  useEffect(() => {
    if (briefData?.contentBrief) {
      track("Content brief viewed", {
        id: briefData.contentBrief.id,
        brand: activeBrandId,
        briefName: briefData.contentBrief.name,
        status: briefData.contentBrief.status,
        deliverableCount: briefData.contentBrief.deliverables?.length || 0,
        budget: briefData.contentBrief.budget,
        dueDate: briefData.contentBrief.dueDate,
        locations: briefData.contentBrief.locations?.map((l) => l.id) || [],
        creatorId: briefData.contentBrief.bookings?.[0]?.creator?.id,
      });
    }
  }, [briefData?.contentBrief, track, activeBrandId]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !briefData?.contentBrief) {
    return (
      <Page>
        <H2>Oops</H2>
        <Text margin={"0 0 xl 0"}>
          Couldn't load this brief, please try again later.
        </Text>
      </Page>
    );
  }

  const handleAcceptMatch = () => {
    if (
      !briefData.contentBrief.bookings ||
      briefData.contentBrief.bookings.length === 0
    ) {
      console.error("No bookings available to accept");
      return;
    }

    updateContentBriefBookingMutation.mutate(
      {
        input: {
          id: briefData.contentBrief.bookings[0].id,
          status: ContentBriefBookingStatus.InProgress,
        },
      },
      {
        onSuccess: () => {
          updateContentBriefMutation.mutate(
            {
              input: {
                id: briefData.contentBrief.id,
                status: ContentBriefStatus.InProgress,
              },
            },
            {
              onSuccess: () => {
                // Track content brief match approval
                track("Content brief match approved", {
                  id: briefData.contentBrief.id,
                  brand: activeBrandId,
                  briefName: briefData.contentBrief.name,
                  status: ContentBriefStatus.InProgress,
                  deliverableCount:
                    briefData.contentBrief.deliverables?.length || 0,
                  budget: briefData.contentBrief.budget,
                  dueDate: briefData.contentBrief.dueDate,
                  locations:
                    briefData.contentBrief.locations?.map((l) => l.id) || [],
                  creatorId: briefData.contentBrief.bookings[0].creator?.id,
                  bookingId: briefData.contentBrief.bookings[0].id,
                });

                refetchBrief();
                triggerConfetti();
              },
            }
          );
        },
      }
    );
  };

  const handleDeclineMatch = () => {
    if (
      !briefData.contentBrief.bookings ||
      briefData.contentBrief.bookings.length === 0
    ) {
      console.error("No bookings available to decline");
      return;
    }

    updateContentBriefBookingMutation.mutate(
      {
        input: {
          id: briefData.contentBrief.bookings[0].id,
          status: ContentBriefBookingStatus.Rejected,
        },
      },
      {
        onSuccess: () => {
          updateContentBriefMutation.mutate(
            {
              input: {
                id: briefData.contentBrief.id,
                status: ContentBriefStatus.Pending,
              },
            },
            {
              onSuccess: () => {
                // Track content brief match rejection
                track("Content brief match rejected", {
                  id: briefData.contentBrief.id,
                  brand: activeBrandId,
                  briefName: briefData.contentBrief.name,
                  status: ContentBriefStatus.Pending,
                  deliverableCount:
                    briefData.contentBrief.deliverables?.length || 0,
                  budget: briefData.contentBrief.budget,
                  dueDate: briefData.contentBrief.dueDate,
                  locations:
                    briefData.contentBrief.locations?.map((l) => l.id) || [],
                  creatorId: briefData.contentBrief.bookings[0].creator?.id,
                  bookingId: briefData.contentBrief.bookings[0].id,
                });

                refetchBrief();
              },
            }
          );
        },
      }
    );
  };

  const handleMessageCreator = () => {
    if (
      !briefData.contentBrief.bookings ||
      briefData.contentBrief.bookings.length === 0
    ) {
      console.error("No bookings available to message");
      return;
    }

    if (briefData.contentBrief.bookings[0].creator?.id) {
      setIsChatOpen(!isChatOpen);
    }
  };

  const activeDeliverable = briefData.contentBrief.deliverables.find(
    (deliverable) => deliverable.id === selectedMediaIdForReview
  );

  return (
    <>
      <Page>
        <Flex>
          <Flex margin="0 0 m" justify="center">
            <FakeBackLinkWithText
              margin="0"
              text="Back"
              onClick={() =>
                history.push(
                  `/b/briefs/${getBriefStatusPage(
                    briefData.contentBrief.status
                  )}`
                )
              }
            />
          </Flex>
        </Flex>

        <Grid>
          <Card padding="m l l">
            <Flex justify="space-between" align="flex-start">
              <View margin="0 0 0" style={{ flex: 1, minWidth: 0 }}>
                <H2 margin="0 0 0" style={{ wordBreak: "break-word" }}>
                  {briefData.contentBrief.name}
                </H2>
              </View>
              <View margin="0 0 0 l" style={{ flexShrink: 0 }}>
                <Button
                  size="xs"
                  buttonType="secondary"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  View Brief
                </Button>
              </View>
            </Flex>
            <BriefDetailsTab
              briefData={briefData.contentBrief}
              setSelectedMediaIdForReview={setSelectedMediaIdForReview}
            />
          </Card>
          <CreatorMatch
            contentBrief={briefData.contentBrief}
            onAcceptMatch={handleAcceptMatch}
            onDeclineMatch={handleDeclineMatch}
            onMessageCreator={handleMessageCreator}
          />
        </Grid>
      </Page>
      <Drawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(!isChatOpen)}
        position="right"
        width={480}
      >
        {isChatOpen &&
          activeBrandId &&
          briefData.contentBrief.bookings &&
          briefData.contentBrief.bookings.length > 0 &&
          briefData.contentBrief.bookings[0].creator?.id && (
            <Chat
              brandId={activeBrandId}
              creatorId={briefData.contentBrief.bookings[0].creator.id}
              contentBriefBookingId={briefData.contentBrief.bookings[0].id}
            />
          )}
      </Drawer>
      <Drawer
        isOpen={showPreview}
        onClose={() => setShowPreview(!showPreview)}
        position="right"
        width={400}
      >
        {showPreview && (
          <PreviewBrief
            description={briefData.contentBrief.description}
            deliverables={briefData.contentBrief.deliverables}
            title={briefData.contentBrief.name}
            dueDate={briefData.contentBrief.dueDate ?? undefined}
            image={briefData.contentBrief.coverImage}
            budget={
              briefData.contentBrief.budget
                ? briefData.contentBrief.budget / 100
                : 0
            }
          />
        )}
      </Drawer>
      {activeDeliverable &&
        briefData.contentBrief.bookings &&
        briefData.contentBrief.bookings.length > 0 && (
          <DeliverableMediaReview
            isOpen={!!selectedMediaIdForReview}
            setIsOpen={() => setSelectedMediaIdForReview(null)}
            deliverable={activeDeliverable}
            contentBriefBookingId={briefData.contentBrief.bookings[0].id}
            onReview={(approved: boolean) => {
              setSelectedMediaIdForReview(null);
              refetchBrief();
              if (approved) {
                triggerConfetti();
              }
            }}
          />
        )}
    </>
  );
};

const Grid = styled.div`
  display: grid;
  min-width: 100%;
  grid-template-columns: minmax(66%, 2fr) minmax(200px, 1fr);
  gap: ${(p) => p.theme.spacing.l};
  align-items: start;
  padding-bottom: ${(p) => p.theme.spacing.xl};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
`;

const MasonryGrid = styled(Masonry)`
  &.masonry-grid {
    display: flex;
    width: 100%;

    div {
      &.masonry-grid-column {
        padding-left: ${(p) => p.theme.spacing.l};
        max-width: 260px;

        &:first-child {
          padding-left: 0;
        }

        /* Add spacing between rows */
        > div {
          margin-bottom: ${(p) => p.theme.spacing.l};

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    &.masonry-grid {
      margin-left: 0;

      div.masonry-grid-column {
        padding-left: 0;
      }
    }
  }
`;

const BriefDetailsTab = ({
  briefData,
  setSelectedMediaIdForReview,
}: {
  briefData: BrandContentBriefQuery["contentBrief"];
  setSelectedMediaIdForReview: (mediaId: string) => void;
}) => {
  const history = useHistory();

  return (
    <View margin="0 0 0">
      {briefData.dueDate && (
        <Text margin="0 0 0" size="s">
          To be delivered by{" "}
          {format(fromUnixTime(briefData.dueDate), "do MMMM yyyy")}
        </Text>
      )}
      <View margin="l 0 0">
        <Text weight="bold" margin="0 0 xs" colorPreset="heading">
          Deliverables
        </Text>
        <Underline selected={true} />
        <View margin="l 0 0">
          {briefData.deliverables.length > 0 ? (
            <MasonryGrid
              breakpointCols={{ default: 2, 700: 1 }}
              className="masonry-grid"
              columnClassName="masonry-grid-column"
            >
              {briefData.deliverables
                .sort((a, b) => {
                  // Sort videos first, then images
                  if (
                    a.mediaType === MediaType.Video &&
                    b.mediaType !== MediaType.Video
                  )
                    return -1;
                  if (
                    a.mediaType !== MediaType.Video &&
                    b.mediaType === MediaType.Video
                  )
                    return 1;
                  return 0;
                })
                .map((deliverable) =>
                  Array.from({ length: deliverable.quantity }).map((_, i) => (
                    <DeliverableItem
                      key={`${deliverable.id}-${i}`}
                      deliverable={deliverable}
                      setSelectedDeliverableId={setSelectedMediaIdForReview}
                    />
                  ))
                )}
            </MasonryGrid>
          ) : (
            <AddDelierableEmptyState
              onClick={() => history.push(`/b/briefs/${briefData.id}/edit`)}
            />
          )}
        </View>
      </View>
    </View>
  );
};

const CreatorMatchSkeleton = ({ matching }: { matching: boolean }) => {
  const theme = useTheme();

  return (
    <MatchingWrap>
      {matching && (
        <SkeletonHeader>
          <Flex
            margin="xs xs xs"
            align="center"
            justify="space-between"
            direction="row"
            style={{ position: "relative", width: "100%" }}
          >
            <View>
              <Text
                weight="bold"
                size="xs"
                color={theme.color.pills.greenValue}
                margin="0 0 0 0"
                isCompact
              >
                Matching in progress
                <DotOne>.</DotOne>
                <DotTwo>.</DotTwo>
                <DotThree>.</DotThree>
              </Text>
              <Text size="xs" colorPreset="secondary" margin="0">
                Please check back again later 😊
              </Text>
            </View>
            <Flex
              align="center"
              justify="center"
              direction="column"
              style={{ marginTop: -6, marginRight: 6 }}
            >
              <MatchingStatusIcon
                color={theme.color.pills.greenValue}
                width={28}
                height={28}
              />
            </Flex>
          </Flex>
        </SkeletonHeader>
      )}
      <SkeletonWrap>
        <View padding="l">
          <SkeletonWrapper align="center" justify="center" direction="column">
            <SkeletonAvatarBG />
            <SkeletonText width="60%" height="24px" />
            <SkeletonText width="40%" height="16px" />
            <SkeletonButtonWrap>
              <SkeletonButton />
              <SkeletonButton />
            </SkeletonButtonWrap>
            <SkeletonDivider />
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonDetailItem key={index}>
                <SkeletonText width="30%" />
                <SkeletonText width="50%" />
              </SkeletonDetailItem>
            ))}
          </SkeletonWrapper>
        </View>
      </SkeletonWrap>
    </MatchingWrap>
  );
};

const CreatorMatch = ({
  contentBrief,
  onAcceptMatch,
  onDeclineMatch,
  onMessageCreator,
}: {
  contentBrief: BrandContentBriefQuery["contentBrief"];
  onAcceptMatch: () => void;
  onDeclineMatch: () => void;
  onMessageCreator: () => void;
}) => {
  const theme = useTheme();

  if (
    contentBrief.status === ContentBriefStatus.Pending ||
    contentBrief.status === ContentBriefStatus.Draft ||
    contentBrief.bookings.length === 0
  ) {
    return (
      <CreatorMatchSkeleton
        matching={contentBrief.status === ContentBriefStatus.Pending}
      />
    );
  }

  const booking = contentBrief.bookings[0];

  // Add safety check for accounts array
  if (!booking.creator.accounts || booking.creator.accounts.length === 0) {
    return <CreatorMatchSkeleton matching={false} />;
  }

  const account = booking.creator.accounts[0];

  return (
    <Card>
      <View padding="l">
        <Flex
          align="center"
          justify="center"
          direction="column"
          style={{ width: "100%" }}
        >
          <div style={{ position: "relative", margin: "0 auto" }}>
            <AvatarBG>
              <AvatarWrap>
                <Avatar
                  borderSize={0}
                  size={82}
                  withShadow={false}
                  url={booking.creator.preferredProfile?.avatar}
                />
              </AvatarWrap>
            </AvatarBG>
            <Flex
              align="center"
              justify="center"
              style={{ position: "absolute", bottom: -7, right: 0, left: 0 }}
            >
              <MatchedTag>
                <div style={{ marginRight: 2, marginBottom: 0 }}>
                  <BoltIcon height={12} width={14} />
                </div>
                Available
              </MatchedTag>
            </Flex>
          </div>
          <Flex
            margin="xl 0 0 0"
            justify="space-between"
            align="center"
            direction="row"
          >
            <div>
              <Text size="xxl" margin="0" isCompact weight="bold">
                {account.firstName} {account.lastName}
              </Text>
            </div>
          </Flex>
          {contentBrief.status === ContentBriefStatus.Matched ? (
            <>
              <ButtonWrap margin="s 0 xs">
                <AcceptMatchButton size="xs" onClick={onAcceptMatch}>
                  Accept Match
                </AcceptMatchButton>
                <DeclineMatchButton size="xs" onClick={onDeclineMatch}>
                  Decline
                </DeclineMatchButton>
              </ButtonWrap>
              <Flex align="center" margin="m 0 0" direction="row">
                <ChatIcon
                  bgColor={theme.color.card.background}
                  fgColor={theme.color.typography.text}
                />
                <Text size="xs" colorPreset="secondary" margin="0 0 0 xs">
                  Accept match to start a conversation
                </Text>
              </Flex>
            </>
          ) : (
            <div style={{ width: "100%" }}>
              <Button
                margin="s 0 0"
                size="s"
                buttonType="secondary"
                onClick={onMessageCreator}
                isDisabled={!booking.creator}
              >
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Flex align="center" direction="row" justify="center">
                    <AirplaneIcon
                      colorPreset="text"
                      height={16}
                      width={16}
                      style={{ marginRight: 6 }}
                    />
                    Message Creator
                  </Flex>
                  {booking.unreadMessageCount > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        right: -4,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <NotificationIndicator>
                        {booking.unreadMessageCount}
                      </NotificationIndicator>
                    </div>
                  )}
                </div>
              </Button>
              <Flex
                align="center"
                margin="m 0 0"
                direction="row"
                justify="center"
              >
                <ChatIcon
                  bgColor={theme.color.card.background}
                  fgColor={theme.color.typography.text}
                />
                <Text
                  size="xs"
                  colorPreset="secondary"
                  align="center"
                  margin="0 0 0 xs"
                >
                  Typically replies in a few hours
                </Text>
              </Flex>
            </div>
          )}

          <CardDivider margin="l 0 xl" />

          {contentBrief.status === ContentBriefStatus.Matched ? (
            <View style={{ width: "100%" }}>
              <DetailList
                fields={[
                  booking.creator.portfolioUrl
                    ? {
                        label: "Portfolio",
                        value: (
                          <ExternalTextLink
                            href={ensureProtocol(booking.creator.portfolioUrl)}
                            target="_blank"
                          >
                            View
                          </ExternalTextLink>
                        ),
                      }
                    : null,
                  {
                    label: "Location",
                    value: `London, UK`,
                  },
                  {
                    label: "Total Cost",
                    value: `£${
                      contentBrief.budget ? contentBrief.budget / 100 : 200
                    }`,
                  },
                  {
                    label: "Communication",
                    value: <StarRating rating={5} />,
                  },
                  {
                    label: "Content Quality",
                    value: <StarRating rating={5} />,
                  },
                  {
                    label: "Punctuality",
                    value: <StarRating rating={5} />,
                  },
                ]}
              />
              {booking.matchReason ? (
                <MatchReason>
                  <Text colorPreset="text" margin="0" weight="semi">
                    “{booking.matchReason}”
                  </Text>
                  <Text
                    size="s"
                    colorPreset="secondary"
                    margin="xs 0 0"
                    weight="semi"
                  >
                    — Team Joli
                  </Text>
                </MatchReason>
              ) : null}
            </View>
          ) : (
            <Timeline brief={contentBrief} />
          )}
        </Flex>
      </View>
    </Card>
  );
};

// Define step statuses
enum StepStatus {
  Completed = "completed",
  InProgress = "in-progress",
  Upcoming = "upcoming",
}

// Define the specific names our timeline steps can have
type TimelineStepName =
  | "Publish Brief"
  | "Approve Creator"
  | "Arrange Shoot"
  | "Review Content"
  | "Sign Off & Complete";

// Define the structure for a timeline step using the specific name type
interface TimelineStep {
  name: TimelineStepName;
  status: StepStatus;
}

// Define the order and names of the timeline steps using the specific type
const timelineStepNames: TimelineStepName[] = [
  "Publish Brief", // 0
  "Approve Creator", // 1
  "Arrange Shoot", // 2
  "Review Content", // 3
  "Sign Off & Complete", // 4
];

// Map Brief Status directly to the index of the ACTIVE step.
// If status is Completed, all steps are completed (index > last index).
// Handle terminal states like Rejected/Cancelled specifically.
const statusToActiveIndexMap: Partial<Record<ContentBriefStatus, number>> = {
  [ContentBriefStatus.Draft]: 0,
  [ContentBriefStatus.Pending]: 1,
  [ContentBriefStatus.Matched]: 1,
  [ContentBriefStatus.InProgress]: 2,
  [ContentBriefStatus.InReview]: 3,
  [ContentBriefStatus.Complete]: 5,
};

// Styled Components for the Timeline
const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p: ThemeProp) => p.theme.spacing.m};
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: ${(p) => p.theme.spacing.s};
`;

const TimelineConnector = styled.div<{
  isLast: boolean;
  theme: ThemeProp["theme"];
}>`
  position: absolute;
  left: -9px;
  top: 4px;
  bottom: ${(props) => (props.isLast ? "auto" : "-22px")};
  height: ${(props) => (props.isLast ? "calc(50% - 4px)" : "auto")};
  width: 1px;
  background-color: ${(p) => p.theme.color.card.divider};
`;

const TimelineIconWrapper = styled(View)<{
  status: StepStatus;
  theme: ThemeProp["theme"];
}>`
  position: absolute;
  left: -20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background-color: ${(p) => p.theme.color.card.background};
`;

const StepText = styled(Text)<{
  status: StepStatus;
  theme: ThemeProp["theme"];
}>`
  margin-top: 1px;
  margin-left: ${(p: ThemeProp) =>
    p.theme.spacing.l}; // Space between icon and text
  color: ${(p) =>
    p.status === StepStatus.Upcoming
      ? p.theme.color.typography.secondary
      : p.theme.color.typography.text};
  font-weight: ${(p) =>
    p.status === StepStatus.InProgress
      ? p.theme.typography.weight.semi
      : p.theme.typography.weight.normal};
`;

// Helper component to render the correct icon based on status
const StatusIcon = ({ status }: { status: StepStatus }) => {
  switch (status) {
    case StepStatus.Completed:
      return <CheckCircle checked={true} size="xl" />;
    case StepStatus.InProgress:
      return (
        <ActiveCirlceWrap>
          <ActiveCircle />
        </ActiveCirlceWrap>
      );
    case StepStatus.Upcoming:
    default:
      return <CheckCircle checked={false} size="xl" />;
  }
};

const ActiveCirlceWrap = styled.div`
  border-radius: 999px;
  display: flex;
  border: 1.5px solid ${(p) => p.theme.color.primary};
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 2px;
  box-shadow: 0px 0px 0px 2px ${(p) => p.theme.color.input.hover};
  outline: none;
`;

const ActiveCircle = styled.div`
  border-radius: 999px;
  background-color: ${(p) => p.theme.color.primary};
  width: 14px;
  height: 14px;
`;

const Timeline = ({
  brief,
}: {
  brief: BrandContentBriefQuery["contentBrief"];
}) => {
  // Get the index of the step considered "active" based on the brief's status.
  // Default to -1 (all upcoming) if status is unknown or not mapped.
  const activeIndex = statusToActiveIndexMap[brief.status] ?? -1;

  // Generate steps with calculated statuses based on the active index
  const steps: TimelineStep[] = timelineStepNames.map((name, index) => {
    let status: StepStatus;
    if (index < activeIndex) {
      status = StepStatus.Completed;
    } else if (index === activeIndex) {
      // Check if the activeIndex points to a valid step (not the 'Completed' case)
      status =
        index < timelineStepNames.length
          ? StepStatus.InProgress
          : StepStatus.Completed; // Should be Completed if activeIndex signifies overall completion
    } else {
      status = StepStatus.Upcoming;
    }

    // Special override for the fully Completed status
    if (activeIndex === timelineStepNames.length) {
      status = StepStatus.Completed;
    }

    return { name, status };
  });

  return (
    <View style={{ width: "100%", boxSizing: "border-box" }}>
      <View margin="0 xl">
        <TimelineContainer>
          {steps.map((step, index) => (
            <TimelineItem key={step.name}>
              <TimelineConnector isLast={index === steps.length - 1} />
              <TimelineIconWrapper status={step.status}>
                <StatusIcon status={step.status} />
              </TimelineIconWrapper>
              <StepText status={step.status} margin="0 0 0 s" isInline>
                {step.name}
              </StepText>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </View>
    </View>
  );
};

const MatchedTag = styled.span`
  background: ${(p) => p.theme.color.tags.lightGreen};
  color: ${(p) => p.theme.color.tags.darkGreen};
  padding: 2px ${(p) => p.theme.spacing.m} 0px 9px;
  border-radius: 999px;
  font-size: ${(p) => p.theme.typography.size.xs};
  font-weight: ${(p) => p.theme.typography.weight.semi};
  display: flex;
  align-items: center;
  box-shadow: ${(p) => p.theme.shadow.imageFloatingIcon};
`;

const AvatarBG = styled.div`
  background: ${(p) => p.theme.color.brandGradient};
  border-radius: 999px;
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarWrap = styled.div`
  background: ${(p) => p.theme.color.background.behind};
  border-radius: 999px;
  border: 2px solid ${(p) => p.theme.color.card.background};
`;

const AddDelierableWrap = styled.div`
  border: 2px dashed ${(p) => p.theme.color.typography.secondary}70;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  padding: ${(p) => p.theme.spacing.xl} ${(p) => p.theme.spacing.m}
    ${(p) => p.theme.spacing.m};
  margin-top: ${(p) => p.theme.spacing.l};
  cursor: pointer;
  transition: opacity 0.1s ease-in-out, border-color 0.1s ease-in-out;
  text-decoration: none;
  display: block;
  opacity: 0.7;

  :hover {
    border-color: ${(p) => p.theme.color.typography.secondary};
    opacity: 1;
  }
`;

const AddDelierableEmptyState = ({ onClick }: { onClick: () => void }) => {
  return (
    <AddDelierableWrap onClick={onClick}>
      <Flex direction="column" align="center" justify="center" margin="xs 0 m">
        <StoryIcon width={26} colorPreset="secondary" />
        <Text margin="s 0 0" weight="semi" size="s" colorPreset="secondary">
          Add Deliverable
        </Text>
      </Flex>
    </AddDelierableWrap>
  );
};

const AcceptMatchButton = styled(Button)`
  background: ${(p) => p.theme.color.constructive};
  color: #fff;
`;

const DeclineMatchButton = styled(Button)`
  background: ${(p) => p.theme.color.destructive};
  color: #fff;
`;

const MatchReason = styled.div`
  padding: ${(p) => p.theme.spacing.m};
  margin-top: ${(p) => p.theme.spacing.m};
  margin-bottom: ${(p) => p.theme.spacing.xs};
  box-sizing: border-box;
  width: 100%;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  background: ${(p) => p.theme.color.card.callout};
  box-shadow: ${(p) => p.theme.shadow.card};
`;

const dotAnimation = css`
  opacity: 0;
  display: inline-block;
`;

const DotOne = styled.span`
  ${dotAnimation}
  animation: showDot 2s infinite;
  margin-right: 1px;
  margin-left: 1px;
  @keyframes showDot {
    0% {
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
    75% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const DotTwo = styled.span`
  ${dotAnimation}
  animation: showDot 2s infinite;
  animation-delay: 0.2s;
  margin-right: 1px;
`;

const DotThree = styled.span`
  ${dotAnimation}
  animation: showDot 2s infinite;
  animation-delay: 0.4s;
  margin-right: 1px;
`;

const SkeletonWrap = styled.div`
  background: ${(p) => p.theme.color.card.background};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  box-shadow: ${(p) => p.theme.shadow.cardMedium};
  padding: ${(p) => p.theme.spacing.l};
`;

const MatchingWrap = styled.div`
  background: ${(p) => p.theme.color.pills.greenBackground}90;
  border-radius: ${(p) => p.theme.misc.borderRadius};
  box-shadow: ${(p) => p.theme.shadow.card};
  padding: ${(p) => p.theme.spacing.s};
`;
